import {
    Alert,
    Box,
    Divider,
    Grid,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Dropdown from "../../components/Input/DropdownV2";
import Iconify from "../../components/iconify";
import { LoadingButton } from "@mui/lab";
import alert from "../../util/alerts";
import { policyService } from "../../services/api/policyServics";
import UploadPolicyContainer from "./upload-policy-container";
import { PolicySavePayloadDTO } from "../../types";
import HealthPolicyForm from "./health-policy-form";
import MotorPolicyForm from "./motor-policy-form";
import TermPolicyForm from "./term-policy-form";
import "./upload-policy.scss";
import { useQueryClient } from "react-query";

export default function UploadPolicy() {
    const theme = useTheme();
    const user = JSON.parse(sessionStorage.getItem("user") || "{}");
    const navigate = useNavigate();

    const [selectInsuranceCategory, setSelectInsuranceCategory] = useState(1);
    const [file, setFile] = useState<File | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [step, setStep] = useState(1);
    const [policyData, setPolicyData] = useState<PolicySavePayloadDTO | null>(
        null
    );
    const [reScanPDF, setReScanPDF] = useState(0);
    const [pdfUuid, setPdfUuid] = useState<string | null>(null);
    const queryClient = useQueryClient();
    const [customer, setCustomer] = useState<{
        label: string;
        value: string;
    } | null>(null);

    useEffect(() => {
        policyService.getEmployeeCustomer().then((res) => {
            if (
                res.data &&
                res.data.response?.label &&
                res.data.response?.value
            ) {
                setCustomer({
                    label: res.data.response.label,
                    value: res.data.response.value,
                });
            } else {
                setCustomer(null);
            }
        });
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0 });
        console.log("scroll to top");
        const mainContent =
            document.getElementsByClassName("body-container_v2")[0];
        if (mainContent) {
            mainContent.scrollTo({ top: 0, left: 0 });
        }
    }, [step]);

    const handleUploadFile = () => {
        if (!file) {
            alert("Please select file", "error");
            return;
        }

        if (!selectInsuranceCategory) {
            alert("Please select insurance category", "error");
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            alert("File size exceeds 10MB", "error");
            return;
        }

        if (!user.agentUuid) {
            alert("Agent uuid not found", "error");
            return;
        }

        setIsLoading(true);

        policyService
            .uploadPersonalPolicyPDF(file, user.agentUuid)
            .then((resData) => {
                if (resData.length > 0) {
                    setPolicyData(
                        resData[0].thirdPartyJsonData.agentCRMPolicyDTO
                    );
                    setPdfUuid(resData[0].uuid);
                }

                setStep(2);
            })
            .catch((err) => {
                alert(
                    err?.response?.data?.message || "Could not upload file",
                    "error"
                );
                setReScanPDF((prev) => prev + 1);
                setIsLoading(false);
            })
            .finally(() => {
                setIsLoading(false);
                // setFile(null);
            });
    };

    const handleReScanPDF = () => {
        if (user.agentUuid && pdfUuid) {
            setIsLoading(true);
            policyService
                .rescanPDF(user.agentUuid, pdfUuid)
                .then((resData) => {
                    setPolicyData(resData.thirdPartyJsonData.agentCRMPolicyDTO);
                    setStep(2);
                })
                .catch((err) => {
                    alert(
                        err?.response?.data?.message ||
                            "Could not re-scan file",
                        "error"
                    );
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    };

    const onSubmit = (payload: PolicySavePayloadDTO) => {
        console.log(payload);

        if (!pdfUuid) {
            alert("PDF UUID is required", "error");
            return;
        }

        payload = {
            ...payload,
            flow: 3,
            isPolicyReader: true,
            policyReaderDataUuid: pdfUuid,
        };

        policyService
            .savePersonalPolicy(payload)
            .then(() => {
                queryClient.invalidateQueries(["policyCards"]);
                navigate("/plans");
            })
            .catch((err) => {
                console.log(
                    err?.response?.data?.message || "Could not save policy",
                    "error"
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const renderForm = () => {
        if (customer && customer?.value) {
            switch (selectInsuranceCategory) {
                case 1:
                    return (
                        <HealthPolicyForm
                            onSubmit={onSubmit}
                            policyData={policyData}
                            isLoading={isLoading}
                            setIsLoading={setIsLoading}
                            customer={customer}
                        />
                    );
                case 2:
                    return (
                        <MotorPolicyForm
                            onSubmit={onSubmit}
                            policyData={policyData}
                            isLoading={isLoading}
                            customer={customer}
                        />
                    );
                case 3:
                    return (
                        <TermPolicyForm
                            onSubmit={onSubmit}
                            policyData={policyData}
                            isLoading={isLoading}
                            customer={customer}
                        />
                    );
                default:
                    return null;
            }
        }
    };

    if (step === 2 && selectInsuranceCategory) {
        return (
            <UploadPolicyContainer
                subHeading="Step 2/2"
                heading={"Review and Edit your policy details"}
                back={() => {
                    setFile(null);
                    setPolicyData(null);
                    setReScanPDF(0);
                    setPdfUuid(null);
                    setStep(1);
                }}
            >
                {renderForm()}
            </UploadPolicyContainer>
        );
    }

    return (
        <UploadPolicyContainer
            subHeading="Step 1/2"
            heading="Upload personal policies"
            back={() => {
                navigate(-1);
            }}
        >
            <Alert
                severity="info"
                sx={{
                    padding: "16px 15px",
                    borderRadius: "16px",
                }}
            >
                Only personal policies are allowed, Your data will not be
                shared!
            </Alert>
            <Grid
                container
                spacing={2}
                columnSpacing={{ xs: "0px", sm: "40px" }}
                sx={{
                    paddingTop: "24px",
                    minHeight: { xs: "100%", sm: "456px" },
                }}
            >
                <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" mb={2}>
                        Select Insurance category
                    </Typography>
                    <Dropdown
                        name="selectInsuranceCategory"
                        items={[
                            { id: 1, name: "Health insurance" },
                            { id: 2, name: "Motor insurance" },
                            { id: 3, name: "Term Life insurance" },
                        ]}
                        variant="outlined"
                        change={(val: number) => {
                            setSelectInsuranceCategory(val);
                        }}
                        defaultVal={selectInsuranceCategory}
                        autoFocus
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" mb={2}>
                        Upload policy
                    </Typography>
                    <input
                        accept="application/pdf"
                        id="upload-policy-file"
                        type="file"
                        style={{ display: "none" }}
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                                console.log("Uploaded file:", file.name);
                                setFile(file);
                            }
                        }}
                    />
                    {!file ? (
                        <label
                            htmlFor="upload-policy-file"
                            style={{ width: "100%", cursor: "pointer" }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    border: "1px dashed #C4C4C4",
                                    borderRadius: "8px",
                                    padding: "20px",
                                    textAlign: "center",
                                    backgroundColor: "#F9F9F9",
                                    gap: "4px",
                                }}
                            >
                                <Iconify
                                    icon="material-symbols:upload-file-outline"
                                    height={32}
                                    width={32}
                                />
                                <Typography
                                    variant="body3"
                                    color={theme.palette.text.primary}
                                >
                                    Upload file
                                </Typography>

                                <Typography
                                    variant="body3"
                                    color={theme.palette.text.secondary}
                                >
                                    under 10MB & PDF Format
                                </Typography>
                            </Box>
                        </label>
                    ) : (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "space-between",
                                border: "1px solid #EAEAEF",
                                borderRadius: "16px",
                                padding: "16px",
                                textAlign: "start",
                                gap: "16px",
                            }}
                        >
                            <Typography
                                variant="body3"
                                color={theme.palette.text.primary}
                            >
                                {file.name}
                            </Typography>
                            <Box
                                sx={{
                                    padding: "6px",
                                    borderRadius: "50%",
                                    border: "1px solid #EAEAEF",
                                    height: "28px",
                                    width: "28px",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <Iconify
                                    icon="material-symbols:close"
                                    onClick={() => {
                                        setFile(null);
                                    }}
                                    sx={{
                                        cursor: "pointer",
                                        height: "16px",
                                        width: "16px",
                                    }}
                                />
                            </Box>
                        </Box>
                    )}
                </Grid>
            </Grid>
            <Divider
                variant="dashed"
                sx={{
                    borderColor: "grey.400",
                    opacity: 0.3,
                    my: "16px",
                }}
            />
            <Box
                sx={{
                    display: "flex",
                    justifyContent: { xs: "space-between", sm: "flex-end" },
                    alignItems: "center",
                    flexDirection: { xs: "column", sm: "row" },
                    position: { xs: "fixed", sm: "static" },
                    left: 0,
                    bottom: { xs: 0, sm: "auto" },
                    width: { xs: "100%", sm: "auto" },
                    bgcolor: { xs: "#fff", sm: "transparent" },
                    p: { xs: 2, sm: 0 },
                    boxShadow: {
                        xs: "0 -2px 8px rgba(0,0,0,0.06)",
                        sm: "none",
                    },
                    zIndex: 1201,
                }}
            >
                <LoadingButton
                    size="small"
                    variant="contained"
                    sx={{
                        marginLeft: { xs: 0, sm: "auot" },
                        alignSelf: { xs: "center", sm: "flex-end" },
                        background: theme.palette.brand.main,
                        width: { xs: "100%", sm: "auto" },
                    }}
                    loading={isLoading}
                    disabled={!file || !selectInsuranceCategory || !customer}
                    onClick={() => {
                        if (reScanPDF > 0 && pdfUuid) {
                            handleReScanPDF();
                        } else {
                            handleUploadFile();
                        }
                    }}
                >
                    {reScanPDF > 0 && pdfUuid ? "Re-Scan" : "Submit"}
                </LoadingButton>
            </Box>
        </UploadPolicyContainer>
    );
}
